/**
 * Desktop floating windows: frames dragged out of focus across a display
 * edge, drawn on the shared frame window shell above this webview. A window
 * this display owns is interactive; a window owned by a neighbouring display
 * shows as an inert continuation while its drag crosses this display.
 */
import { cn } from "@/lib/cn";
import "./floating-frame-continuation.css";
import { useWorkspaceLoadStore } from "@/stores/workspace-load-store";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  FrameWindowShell,
  type FrameWindowDrag,
} from "@/components/focus/frame-window-shell";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { useFrame } from "@/stores/frames-selectors";
import { useProjectStore } from "@/stores/project-store";
import {
  closeFloatingFrame,
  raiseFloatingFrame,
  updateFloatingFrameBox,
  useFloatingFrameStore,
  type FloatingFrame,
} from "./floating-frame-store";
import {
  beginFloatingFrameDrag,
  toggleFloatingFrameWindow,
} from "./floating-frame-drag";
import { floatingFrameRenderer } from "./floating-frame-renderer";
import { floatingFrameVisibleOnDisplay } from "./floating-frame-visibility";
import { requestNativeGlassRefresh } from "./use-native-glass-surfaces";
import { useWindowOrigin } from "./use-window-origin";
import { getWindowDisplayId } from "./use-window-role";

interface Origin {
  x: number;
  y: number;
}

/** Header drag: the global floating drag moves the window, also across
 *  displays. Its window-level pointerup ends the drag, even after this
 *  window unmounted. */
function floatingWindowDrag(frameId: string): FrameWindowDrag {
  return {
    start(_pointer, box, press) {
      const frame = useFloatingFrameStore.getState().frames[frameId];
      if (frame?.displayId === getWindowDisplayId())
        beginFloatingFrameDrag(frame, press);
      return box;
    },
    move: () => null,
    end() {},
    cancel() {},
  };
}

/** A frame deleted from this display's workspace closes its window, once the
 *  frame load settled. A transient chat exists only in its window. */
function useCloseDeletedFrame(
  floating: FloatingFrame,
  inStore: boolean,
  owned: boolean,
) {
  const spaceId = useProjectStore((state) => state.currentSpace?.id);
  const settled = useWorkspaceLoadStore(
    (state) => !state.isLoadingCanvas && state.framesLoadedSpaceId === spaceId,
  );
  const seen = useRef(false);
  const transient =
    floating.frame.metadata?.transientConversationFrame === true;
  const { frameId } = floating;
  useEffect(() => {
    if (!owned || !settled) return;
    if (inStore) seen.current = true;
    else if (seen.current && !transient) closeFloatingFrame(frameId);
  }, [frameId, inStore, owned, settled, transient]);
}

interface FloatingFrameWindowProps {
  floating: FloatingFrame;
  origin: Origin;
  owned: boolean;
}

const FloatingFrameWindow = memo(function FloatingFrameWindow({
  floating,
  origin,
  owned,
}: FloatingFrameWindowProps) {
  const { frameId } = floating;
  const stored = useFrame(frameId);
  useCloseDeletedFrame(floating, !!stored, owned);
  // A card-only store copy (a wall summary) holds less than the frame the
  // window carries; only a full copy is newer.
  const frame =
    stored && (stored.contentState ?? "full") === "full"
      ? stored
      : floating.frame;
  const { x, y, width, height } = floating.box;
  const box = useMemo(
    () => ({ x: x - origin.x, y: y - origin.y, width, height }),
    [height, origin.x, origin.y, width, x, y],
  );
  const drag = useMemo(() => floatingWindowDrag(frameId), [frameId]);
  const activate = useCallback(() => {
    if (owned) raiseFloatingFrame(frameId);
  }, [frameId, owned]);
  const toggle = useCallback(
    () => toggleFloatingFrameWindow(frameId),
    [frameId],
  );
  const resizeEnd = useCallback(
    (resized: FocusWindowBox) =>
      updateFloatingFrameBox(frameId, {
        ...resized,
        x: resized.x + origin.x,
        y: resized.y + origin.y,
      }),
    [frameId, origin.x, origin.y],
  );
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0",
        !owned && "floating-frame-continuation",
      )}
      style={{ zIndex: floating.order ?? 1 }}
      data-floating-frame-continuation={owned ? undefined : "true"}
      aria-hidden={owned ? undefined : true}
      inert={!owned}
    >
      <FrameWindowShell
        testIdPrefix="floating-frame"
        frame={frame}
        selected
        box={box}
        renderer={floatingFrameRenderer}
        drag={drag}
        onActivate={activate}
        onToggle={toggle}
        onResizeEnd={resizeEnd}
      />
    </div>
  );
});

function FloatingFrameSurface() {
  const frames = useFloatingFrameStore((state) => state.frames);
  const projectId = useProjectStore((state) => state.currentProject?.id);
  const spaceId = useProjectStore((state) => state.currentSpace?.id);
  const { x: originX, y: originY } = useWindowOrigin();
  const origin = useMemo(
    () => ({ x: originX, y: originY }),
    [originX, originY],
  );
  const displayId = getWindowDisplayId();
  const visibleFrames = useMemo(
    () =>
      Object.values(frames).filter(
        (frame) =>
          floatingFrameVisibleOnDisplay(frame, displayId, {
            ...origin,
            width: window.innerWidth,
            height: window.innerHeight,
          }) &&
          (frame.otherProject ||
            (frame.projectId === projectId && frame.spaceId === spaceId)),
      ),
    [displayId, frames, origin, projectId, spaceId],
  );

  useLayoutEffect(() => {
    requestNativeGlassRefresh({ trackTransition: false });
  }, [origin, visibleFrames]);
  useLayoutEffect(() => () => requestNativeGlassRefresh(), []);

  if (visibleFrames.length === 0) return null;

  return (
    <div
      data-floating-frame-layer="true"
      data-testid="floating-frame-layer"
      className="pointer-events-none fixed inset-0 z-[70]"
    >
      {visibleFrames.map((frame) => {
        const preview = frame.snapPreview;
        return preview ? (
          <div
            key={`${frame.frameId}-snap-preview`}
            aria-hidden="true"
            data-testid={`floating-frame-snap-preview-${preview.mode}`}
            className="pointer-events-none absolute rounded-frame border border-accent/55 bg-accent/10"
            style={{
              left: preview.box.x - origin.x,
              top: preview.box.y - origin.y,
              width: preview.box.width,
              height: preview.box.height,
            }}
          />
        ) : null;
      })}
      {visibleFrames.map((frame) => (
        <FloatingFrameWindow
          key={frame.frameId}
          floating={frame}
          origin={origin}
          owned={frame.displayId === displayId}
        />
      ))}
    </div>
  );
}

/** Above every workspace surface of this webview, in every desktop mode. */
export function FloatingFrameLayer() {
  return createPortal(<FloatingFrameSurface />, document.body);
}
